import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { AUTH_LOGOUT } from 'actions/types'
import { HeaderMenu } from 'components/HeaderMenu'
import { LogoBlue } from 'components/Logo'
import { COMPANY_NAME, IS_MAIN_COMPANY } from 'config'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { convertUrl } from 'utils'

import { renderNav } from '../utils'

/** The nav a company gets before it touches Admin > Configuration > Interface > Vendor Portal. */
const DEFAULT_NAV = [{ key: 'pipeline', title: 'Pipeline', link: '/vendorPortal', value: true, order: 1 }]

export function VendorHeader() {
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const { auth, menu, logos } = useSelector((state: any) => ({
    auth: state.auth,
    menu: state.header.menu.vendorPortal || {},
    logos: state.header.menu.logos || {},
  }))

  const location = useLocation()
  const dispatch = useDispatch()

  // The top nav, from the company's own `vendorPortal` section. It was a hardcoded Pipeline link
  // until now, so this was the one header with nothing configurable about it at all.
  const navItems = useMemo(() => {
    const saved = Object.entries(menu).map(([key, item]: [string, any]) => ({ key, ...item }))
    return (saved.length ? saved : DEFAULT_NAV)
      .filter((item: any) => item?.value !== false)
      .map((item: any, index: number) => ({
        key: item.key,
        name: item.title || item.key,
        href: item.link || `/${String(item.key).toLowerCase()}`,
        order: item.order ?? index + 1,
      }))
      .sort((a: any, b: any) => a.order - b.order)
  }, [menu])

  const activeMenu = (to: string) => {
    if (to === '/vendorPortal' && location.pathname.startsWith('/vendorPortal')) return 'shadow'
    return location.pathname === to ? 'shadow' : ''
  }

  const handleLogout = () => {
    dispatch({ type: AUTH_LOGOUT })
    window.location.href = IS_MAIN_COMPANY ? '/' : '/login'
  }

  const renderLogo = () => {
    const content = (
      <>
        <span className="sr-only">{COMPANY_NAME}</span>
        <LogoBlue />
      </>
    )
    const customLink = logos?.['vendorPortal']
    if (customLink) return <a href={convertUrl(customLink)}>{content}</a>
    return <Link to="/vendorPortal">{content}</Link>
  }

  return (
    <div>
      <div className="max-w-screen-2xl m-auto relative py-4 px-2 bg-white">
        <nav className="relative flex items-center justify-between" aria-label="Global">
          <div className="flex items-center flex-grow flex-shrink-0 lg:flex-grow-0">
            <div className="flex items-center justify-between w-full md:w-auto">
              {renderLogo()}
              <div className="mr-2 flex items-center md:hidden">
                <div
                  className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 hover:outline-none hover:ring-2 hover:ring-inset hover:ring-primary"
                  onClick={() => setShowMobileMenu(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden flex-wrap items-center md:flex md:ml-10 md:pr-4 md:space-x-8">
            {navItems.map((item: any, index: number) =>
              renderNav(
                item,
                `nav-${index}`,
                `hover:shadow font-variation-settings-600 px-3 py-2 my-2 text-gray-600 hover:text-gray-900 ${activeMenu(
                  item.href,
                )}`,
              ),
            )}
            {auth.isAuthenticated && <HeaderMenu onLogout={handleLogout} />}
          </div>
        </nav>
      </div>
      {showMobileMenu && (
        <div>
          <div className="absolute z-10 top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden">
            <div className="rounded-lg shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
              <div className="px-5 pt-4 flex items-center justify-between">
                <div>
                  <LogoBlue />
                </div>
                <div className="-mr-2">
                  <div
                    className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 hover:outline-none hover:ring-2 hover:ring-inset hover:ring-primary"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span className="sr-only">Close main menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                </div>
              </div>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item: any, index: number) =>
                  renderNav(
                    item,
                    `${index}`,
                    `block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50 ${activeMenu(
                      item.href,
                    )}`,
                  ),
                )}
                {auth.isAuthenticated && (
                  <div
                    className="block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50"
                    onClick={handleLogout}
                  >
                    Sign Out
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
